import React from 'react';
import Container from '../components/layout/Container';
import { useLanguage } from '../contexts/LanguageContext';
import { getAllStaff } from '../data/staff';
import logo from '../assets/images/logo2.png';
import './About.css';

const About = () => {
    const { t, language } = useLanguage();
    const staff = getAllStaff();
    const keyStaff = staff.slice(0, 5);

    return (
        <div className="about-page">
            {/* Hero Header */}
            <header className="page-header about-hero">
                <Container>
                    <div className="about-hero__content">
                        <h1>
                            Empowering the <span className="text-primary">Next Generation</span> of Tech Leaders.
                        </h1>
                        <p className="text-muted mt-4">
                            Founded on the principles of innovation and accessibility, Da Vinci Institute bridges the gap between academic theory and real-world technological expertise.
                        </p>
                    </div>
                </Container>
            </header>

            {/* History Section */}
            <section className="section">
                <Container>
                    <div className="about-history-grid">
                        <div className="about-history-content reveal reveal--right">
                            <span className="section-subtitle">
                                <span className="material-icons" style={{ fontSize: '14px' }}>history</span>
                                OUR STORY
                            </span>
                            <h2 className="section-title">Decades of Digital Excellence</h2>
                            <div className="about-history-text">
                                {t('about.historyText').split('\n\n').map((paragraph, index) => (
                                    <p key={index}>{paragraph}</p>
                                ))}
                            </div>
                            <div className="about-stats-inline">
                                <div className="about-stat-inline">
                                    <span className="about-stat-inline__number">2000+</span>
                                    <span className="about-stat-inline__label">Graduates</span>
                                </div>
                                <div className="about-stat-inline">
                                    <span className="about-stat-inline__number">50+</span>
                                    <span className="about-stat-inline__label">Courses</span>
                                </div>
                                <div className="about-stat-inline">
                                    <span className="about-stat-inline__number">10+</span>
                                    <span className="about-stat-inline__label">Years</span>
                                </div>
                            </div>
                        </div>
                        <div className="about-history-image reveal reveal--left delay-200">
                            <div className="about-history-image-frame">
                                <img src={logo} alt="Da Vinci Institute" />
                                <div className="about-history-image-overlay"></div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Mission & Vision - matching example */}
            <section className="section bg-white">
                <Container>
                    <div className="mv-grid">
                        <div className="mv-card reveal reveal--up">
                            <div className="mv-card__icon-wrapper">
                                <span className="material-icons mv-card__icon">rocket_launch</span>
                            </div>
                            <h3 className="mv-card__title">{t('sections.mission')}</h3>
                            <p className="mv-card__text">{t('about.missionDesc')}</p>
                        </div>
                        <div className="mv-card reveal reveal--up delay-200">
                            <div className="mv-card__icon-wrapper">
                                <span className="material-icons mv-card__icon">visibility</span>
                            </div>
                            <h3 className="mv-card__title">{t('sections.vision')}</h3>
                            <p className="mv-card__text">{t('about.visionDesc')}</p>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Core Values - matching example */}
            <section className="section">
                <Container>
                    <div className="section-header text-center mb-16 reveal reveal--fade-in">
                        <h2 className="section-title">Values That Drive Us</h2>
                        <p className="section-description">Our culture is built on a foundation of excellence and mutual respect.</p>
                    </div>
                    <div className="values-grid">
                        <div className="value-card reveal reveal--up delay-100">
                            <span className="material-icons value-card__icon">lightbulb</span>
                            <h4 className="value-card__title">Innovation</h4>
                            <p className="value-card__desc">Constantly evolving our curriculum to match industry trends.</p>
                        </div>
                        <div className="value-card reveal reveal--up delay-200">
                            <span className="material-icons value-card__icon">verified_user</span>
                            <h4 className="value-card__title">Integrity</h4>
                            <p className="value-card__desc">Maintaining the highest standards of academic honesty and ethics.</p>
                        </div>
                        <div className="value-card reveal reveal--up delay-300">
                            <span className="material-icons value-card__icon">groups</span>
                            <h4 className="value-card__title">Community</h4>
                            <p className="value-card__desc">Fostering a supportive environment for students of all backgrounds.</p>
                        </div>
                        <div className="value-card reveal reveal--up delay-400">
                            <span className="material-icons value-card__icon">stars</span>
                            <h4 className="value-card__title">Excellence</h4>
                            <p className="value-card__desc">Striving for mastery in everything we teach and practice.</p>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Team Section - matching example */}
            <section className="section bg-white">
                <Container>
                    <div className="about-team">
                        <div className="about-team-header reveal reveal--fade-in">
                            <h3>Our Administrators</h3>

                        </div>
                        <div className="about-team-grid">
                            {keyStaff.map((member, index) => (
                                <div key={member.id} className={`about-team-member reveal reveal--up delay-${(index + 1) * 100}`}>
                                    <div className="about-team-member__img-wrapper">
                                        <img src={member.image} alt={member.name[language]} />
                                    </div>
                                    <h5>{member.name[language]}</h5>
                                    <p className="text-primary text-sm font-medium">{member.position[language]}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Container>
            </section>

            {/* Final CTA */}
            <section className="section">
                <Container>
                    <div className="about-cta reveal reveal--scale">
                        <div className="about-cta__decorative" aria-hidden="true">
                            <div className="about-cta__circle about-cta__circle--1"></div>
                            <div className="about-cta__circle about-cta__circle--2"></div>
                        </div>
                        <div className="about-cta__content">
                            <h2>Ready to start your tech journey?</h2>
                            <p>Join hundreds of successful students who have transformed their careers with Da Vinci Institute. Enrollment is open for next semester.</p>
                            <div className="about-cta__actions">
                                <a href="/contact" className="about-cta__btn about-cta__btn--white">Contact Admissions</a>
                                <a href="/departments" className="about-cta__btn about-cta__btn--outline">View Our Courses</a>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default About;
